/**
 * Decision 211 — reading a distributor's quote (Ingram Micro's "IM Quote"
 * sheet) into the shape the import works from, plus the one piece of
 * arithmetic the feature turns on.
 *
 * Pure: a cell grid in, structured lines out. The workbook itself is unzipped
 * server-side (netlify/functions/_lib/xlsxLite.ts); this file is shared so
 * the unit tests can feed it a grid and the UI can reuse the maths.
 *
 * Margin (Mark, 2026-09-09, answer #1): MARGIN ON SELL, never markup —
 *   sell = cost ÷ (1 − margin)      30% on £2,397.11 → £3,424.44
 * which is exactly how the MS150-24MP-4X template product is priced in HubSpot.
 */

import { round2 } from './money.js'

export type Grid = Map<string, string | number>

export interface SupplierQuoteHeader {
  distributor: string
  reference: string | null
  ccwReference: string | null
  currency: string | null
  exchangeRate: number | null
  /** As printed — "01-Oct-2026 11:59 PM PDT". */
  priceProtectionText: string | null
  /** yyyy-mm-dd when the text could be read as a date. */
  priceProtectionUntil: string | null
  quotedOn: string | null
  salesperson: string | null
  totalExVat: number | null
}

export interface SupplierQuoteLine {
  /** '1.0', '1.1' — Ingram's own numbering; x.1 rows are accessories of x.0. */
  lineRef: string
  parentLineRef: string | null
  sku: string
  description: string
  qty: number
  unitListUsd: number | null
  /** The distributor's unit price in the quote currency = Wave9's unit cost. */
  unitCost: number
  total: number | null
  serviceMonths: number | null
  leadTime: string | null
  sort: number
}

export interface ParsedSupplierQuote {
  header: SupplierQuoteHeader
  lines: SupplierQuoteLine[]
  warnings: string[]
}

/** Catalogue price for a product the import CREATES: cost at the house margin. */
export const CATALOGUE_MARGIN_PCT = 30
/** The margin the import prompt starts on. */
export const DEFAULT_IMPORT_MARGIN_PCT = 30

/** sell = cost ÷ (1 − margin%). A 100% margin is undefined; the caller validates. */
export function sellFromCost(cost: number, marginPct: number): number {
  if (!(marginPct >= 0 && marginPct < 100)) throw new Error('Margin must be from 0% up to (not including) 100%.')
  return round2(cost / (1 - marginPct / 100))
}

/** margin% = (sell − cost) ÷ sell — the inverse, for showing what a typed price means. */
export function marginOnSell(cost: number, sell: number): number | null {
  if (!(sell > 0)) return null
  return round2(((sell - cost) / sell) * 100)
}

// ---------------------------------------------------------------------------
// Grid helpers
// ---------------------------------------------------------------------------

function colIndex(letters: string): number {
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n
}
function colLetters(index: number): string {
  let s = ''
  let n = index
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26) }
  return s
}
function split(ref: string): { col: number; row: number } {
  const m = /^([A-Z]+)(\d+)$/.exec(ref)!
  return { col: colIndex(m[1]), row: Number(m[2]) }
}

const str = (v: string | number | undefined): string | null => {
  if (v == null) return null
  const s = String(v).trim()
  return s === '' ? null : s
}
const num = (v: string | number | undefined): number | null => {
  if (v == null) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const n = Number(v.replace(/[£$,\s]/g, ''))
  return Number.isFinite(n) && v.trim() !== '' ? n : null
}
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

/** Excel serial → yyyy-mm-dd (1900 date system). */
export function serialToIso(serial: number): string {
  const d = new Date(Date.UTC(1899, 11, 30) + Math.round(serial) * 86_400_000)
  return d.toISOString().slice(0, 10)
}

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 }

/** "01-Oct-2026 11:59 PM PDT" / "1 Oct 2026" / "2026-10-01" / serial → yyyy-mm-dd, else null. */
export function readDate(v: string | number | undefined): string | null {
  if (v == null) return null
  if (typeof v === 'number') return v > 20_000 && v < 80_000 ? serialToIso(v) : null
  const s = v.trim()
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (m) return `${m[1]}-${m[2]}-${m[3]}`
  m = /^(\d{1,2})[-\s/]([A-Za-z]{3})[A-Za-z]*[-\s/](\d{4})/.exec(s)
  if (m && MONTHS[m[2].toLowerCase()]) return `${m[3]}-${String(MONTHS[m[2].toLowerCase()]).padStart(2, '0')}-${m[1].padStart(2, '0')}`
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s)   // UK d/m/yyyy
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
  return null
}

// ---------------------------------------------------------------------------
// Ingram Micro "IM Quote"
// ---------------------------------------------------------------------------

/**
 * Header labels sit in one column with the value in the next ("Quote
 * Reference" | "260901 86058374"); the line table starts at the row whose
 * cells include "Cisco Product Code". Both are found by TEXT, not by fixed
 * cell, so a sheet with an extra logo row or a shifted block still reads.
 */
const LINE_COLUMNS: Array<{ key: keyof RawColumns; match: (h: string) => boolean }> = [
  { key: 'lineNo', match: (h) => h === 'line' || h === 'line no' || h.startsWith('line ') },
  { key: 'qty', match: (h) => h === 'qty' || h === 'quantity' },
  { key: 'sku', match: (h) => h.includes('product code') || h === 'sku' || h.includes('part number') },
  { key: 'description', match: (h) => h.includes('description') },
  { key: 'listUsd', match: (h) => h.includes('list price') },
  { key: 'unitPrice', match: (h) => h === 'unit price' || (h.includes('unit price') && !h.includes('list')) },
  { key: 'total', match: (h) => h === 'total price' || h === 'total' || h.startsWith('total price') },
  { key: 'serviceMonths', match: (h) => h.includes('service') && h.includes('month') },
  { key: 'leadTime', match: (h) => h.includes('lead time') },
]
interface RawColumns { lineNo?: number; qty?: number; sku?: number; description?: number; listUsd?: number; unitPrice?: number; total?: number; serviceMonths?: number; leadTime?: number }

export function parseIngramGrid(grid: Grid, distributor = 'Ingram Micro'): ParsedSupplierQuote {
  const warnings: string[] = []
  const at = (col: number, row: number) => grid.get(`${colLetters(col)}${row}`)

  // --- header block: label cell → value in the next column -----------------
  const labelled = new Map<string, string | number>()
  for (const [ref, v] of grid) {
    if (typeof v !== 'string') continue
    const { col, row } = split(ref)
    const right = at(col + 1, row)
    if (right != null) labelled.set(norm(v), right)
  }
  const label = (...names: string[]) => {
    for (const n of names) { const v = labelled.get(norm(n)); if (v != null) return v }
    return undefined
  }

  const header: SupplierQuoteHeader = {
    distributor,
    reference: str(label('Quote Reference', 'Quote Ref', 'Quote Number')),
    ccwReference: str(label('CCW Reference', 'CCW Ref')),
    currency: str(label('Quote Currency', 'Currency'))?.toUpperCase() ?? null,
    exchangeRate: num(label('Exchange Rate')),
    priceProtectionText: str(label('CCW Price Protection Date', 'Price Protection Date', 'Price Protection')),
    priceProtectionUntil: readDate(label('CCW Price Protection Date', 'Price Protection Date', 'Price Protection')),
    quotedOn: readDate(label("Today's Date", 'Date', 'Quote Date')),
    salesperson: str(label('Salesperson', 'Sales Person', 'Account Manager')),
    totalExVat: num(label('Total Price EX VAT', 'Total EX VAT', 'Total (ex VAT)')),
  }
  if (!header.reference) warnings.push('No "Quote Reference" found on the sheet — type one before importing.')
  if (header.currency && header.currency !== 'GBP') warnings.push(`The quote is in ${header.currency}, not GBP — unit prices are imported as-is and become GBP costs.`)

  // --- line table ------------------------------------------------------------
  let headerRow = 0
  for (const [ref, v] of grid) {
    if (typeof v === 'string' && norm(v).includes('product code')) { headerRow = split(ref).row; break }
  }
  if (headerRow === 0) {
    for (const [ref, v] of grid) if (typeof v === 'string' && norm(v) === 'sku') { headerRow = split(ref).row; break }
  }
  if (headerRow === 0) {
    warnings.push('Could not find the line table (no "Cisco Product Code" / "SKU" column header).')
    return { header, lines: [], warnings }
  }
  const cols: RawColumns = {}
  for (const [ref, v] of grid) {
    const { col, row } = split(ref)
    if (row !== headerRow || typeof v !== 'string') continue
    const h = norm(v)
    for (const c of LINE_COLUMNS) if (cols[c.key] == null && c.match(h)) { cols[c.key] = col; break }
  }
  if (cols.sku == null || cols.qty == null || cols.unitPrice == null) {
    warnings.push('The line table needs Product Code, Qty and Unit Price columns.')
    return { header, lines: [], warnings }
  }

  let maxRow = 0
  for (const ref of grid.keys()) maxRow = Math.max(maxRow, split(ref).row)

  const lines: SupplierQuoteLine[] = []
  let lastParent: string | null = null
  for (let row = headerRow + 1; row <= maxRow; row++) {
    const sku = str(at(cols.sku, row))
    const qtyRaw = at(cols.qty, row)
    // The table ends at the totals block ("Total Price EX VAT" sits in a
    // price column with no SKU) — a row with no SKU and no qty is a gap or
    // the end; a label row is skipped, not imported.
    if (!sku) continue
    if (norm(sku).startsWith('total')) break
    const qty = num(qtyRaw)
    const unitCost = num(at(cols.unitPrice, row))
    if (qty == null || unitCost == null) {
      warnings.push(`Row ${row} (${sku}) has no quantity or unit price — skipped.`)
      continue
    }
    const lineNoRaw = cols.lineNo == null ? null : at(cols.lineNo, row)
    const lineRef = lineNoRaw == null
      ? `${lines.length + 1}.0`
      : typeof lineNoRaw === 'number' ? (Number.isInteger(lineNoRaw) ? `${lineNoRaw}.0` : String(lineNoRaw)) : String(lineNoRaw).trim()
    const isAccessory = /\.\d*[1-9]\d*$/.test(lineRef)
    if (!isAccessory) lastParent = lineRef
    lines.push({
      lineRef,
      parentLineRef: isAccessory ? lastParent : null,
      sku,
      description: str(at(cols.description ?? -1, row)) ?? sku,
      qty,
      unitListUsd: cols.listUsd == null ? null : num(at(cols.listUsd, row)),
      unitCost,
      total: cols.total == null ? null : num(at(cols.total, row)),
      serviceMonths: cols.serviceMonths == null ? null : (num(at(cols.serviceMonths, row)) ?? null),
      leadTime: cols.leadTime == null ? null : str(at(cols.leadTime, row)),
      sort: lines.length,
    })
  }
  if (lines.length === 0) warnings.push('The line table is empty.')

  // Totals sanity — Ingram's sheet sums the Total Price column; disagreeing
  // means a row was missed or the sheet has changed shape, worth a look.
  if (header.totalExVat != null && lines.length > 0) {
    const sum = round2(lines.reduce((s, l) => s + (l.total ?? l.qty * l.unitCost), 0))
    if (Math.abs(sum - header.totalExVat) > 0.05) {
      warnings.push(`The lines add up to £${sum.toFixed(2)} but the sheet says £${header.totalExVat.toFixed(2)} ex VAT — check nothing was missed.`)
    }
  }
  return { header, lines, warnings }
}

/** Proper margin per line for a preview / import: the chosen % or a per-line sell override. */
export function priceLine(unitCost: number, marginPct: number, sellOverride?: number | null): number {
  if (sellOverride != null && Number.isFinite(sellOverride) && sellOverride >= 0) return round2(sellOverride)
  return sellFromCost(unitCost, marginPct)
}
